import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, RefreshControl, Alert } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => { const t = await AsyncStorage.getItem('auth_token').catch(() => null); return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const STARS = [1, 2, 3, 4, 5];
const COMPETENCIES = ['Communication', 'Teamwork', 'Problem Solving', 'Leadership', 'Technical Skills', 'Initiative', 'Reliability', 'Customer Focus'];

export default function PerformanceReviewScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || '1';
  const [reviews, setReviews] = useState([]);
  const [currentRating, setCurrentRating] = useState(0);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const bizId = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id') || '1';
      const [profRes, revRes, warnRes, dashRes] = await Promise.allSettled([
        fetch(`${API}/api/company-profile/${parseInt(bizId)||0}`, { headers: h }),
        fetch(`${API}/api/data/employee_performance_reviews?employee_id=${parseInt(empId)||0}`, { headers: h }),
        fetch(`${API}/api/warnings/${parseInt(empId)||0}`, { headers: h }),
        fetch(`${API}/api/employee/dashboard/${parseInt(empId)||0}`, { headers: h }),
      ]);
      if (profRes.status === 'fulfilled' && profRes.value.ok) {
        const pj = await profRes.value.json().catch(() => ({}));
        const prof = pj?.success ? (Array.isArray(pj.data) ? pj.data[0] : pj.data) : null;
        if (prof) { setCompanyProfile(prof); await AsyncStorage.setItem('company_profile', JSON.stringify(prof)); }
      } else { try { const c = await AsyncStorage.getItem('company_profile'); if (c) setCompanyProfile(JSON.parse(c)); } catch {} }
      if (revRes.status === 'fulfilled' && revRes.value.ok) {
        const json = await revRes.value.json().catch(() => ({}));
        setReviews(Array.isArray(json?.data) ? json.data : []);
      }
      if (warnRes.status === 'fulfilled' && warnRes.value.ok) {
        const json = await warnRes.value.json().catch(() => ({}));
        setWarnings(Array.isArray(json?.data) ? json.data : []);
      }
      if (dashRes.status === 'fulfilled' && dashRes.value.ok) {
        const json = await dashRes.value.json().catch(() => ({}));
        if (json?.data?.performance_rating) setCurrentRating(parseFloat(json.data.performance_rating) || 3);
      }
    } catch (e) { console.warn('Performance load:', e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const renderStars = (rating, size = 20) => STARS.map(s => (
    <MaterialCommunityIcons key={s} name={s <= Math.round(rating) ? 'star' : 'star-outline'} size={size} color="#f59e0b" />
  ));

  return (
    <SafeAreaView style={sp.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={sp.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={sp.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={sp.headerTitle}>{companyProfile?.name || 'Performance'}</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        {/* Current Rating */}
        <LinearGradient colors={['#1e7be2', P]} style={sp.ratingHero}>
          <Text style={sp.ratingLabel}>Current Rating</Text>
          <Text style={sp.ratingBig}>{currentRating > 0 ? currentRating.toFixed(1) : '—'}</Text>
          <View style={{ flexDirection: 'row' }}>{renderStars(currentRating, 28)}</View>
          <Text style={sp.ratingDesc}>
            <Text>{currentRating >= 4.5 ? 'Outstanding' : currentRating >= 3.5 ? 'Exceeds Expectations' : currentRating >= 2.5 ? 'Meets Expectations' : currentRating > 0 ? 'Needs Improvement' : 'Not yet rated'}</Text>
          </Text>
        </LinearGradient>

        {/* Company Info */}
        {companyProfile && (
          <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            {companyProfile.industry && <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#EFF6FF', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 }}><MaterialCommunityIcons name="domain" size={14} color={P} /><Text style={{ color: P, fontSize: 11, marginLeft: 4 }}>{companyProfile.industry}</Text></View>}
            {companyProfile.headquarters && <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#EFF6FF', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 }}><MaterialCommunityIcons name="map-marker" size={14} color={P} /><Text style={{ color: P, fontSize: 11, marginLeft: 4 }}>{companyProfile.headquarters}</Text></View>}
          </View>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <View style={sp.section}>
            <Text style={sp.sectionTitle}>Active Warnings</Text>
            {warnings.map((w, i) => (
              <View key={w.id || i} style={sp.warnCard}>
                <View style={[sp.warnIcon, { backgroundColor: w.severity === 'Critical' ? '#fee2e2' : '#fef3c7' }]}>
                  <MaterialCommunityIcons name="alert" size={20} color={w.severity === 'Critical' ? '#ef4444' : '#f59e0b'} />
                </View>
                <View style={{ flex: 1, marginLeft: 12 }}>
                  <Text style={sp.warnType}>{w.warning_type || 'Warning'}</Text>
                  <Text style={sp.warnReason} numberOfLines={2}>{w.reason}</Text>
                  <Text style={sp.warnDate}>{w.issued_date ? new Date(w.issued_date).toLocaleDateString('en-GB') : '—'}</Text>
                </View>
                <View style={[sp.severityBadge, { backgroundColor: w.severity === 'Critical' ? '#fee2e2' : '#fef3c7' }]}>
                  <Text style={[sp.severityText, { color: w.severity === 'Critical' ? '#ef4444' : '#f59e0b' }]}>{w.severity || 'Low'}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Competency Framework */}
        <View style={sp.section}>
          <Text style={sp.sectionTitle}>Competency Framework</Text>
          {COMPETENCIES.map((c, i) => {
            const score = Math.min(5, Math.max(1, Math.round((currentRating || 3) + (Math.sin(i) * 0.8))));
            return (
              <View key={c} style={sp.competencyRow}>
                <Text style={sp.competencyName}>{c}</Text>
                <View style={{ flexDirection: 'row', gap: 2 }}>{renderStars(score, 14)}</View>
              </View>
            );
          })}
        </View>

        {/* Past Reviews */}
        <View style={sp.section}>
          <Text style={sp.sectionTitle}>Review History</Text>
          {reviews.length === 0 ? (
            <Text style={sp.empty}>No reviews on record yet</Text>
          ) : reviews.map((r, i) => (
            <View key={r.id || i} style={sp.reviewCard}>
              <View style={sp.reviewHeader}>
                <Text style={sp.reviewPeriod}>{r.review_period || r.period || `Q${i+1} Review`}</Text>
                {r.overall_rating && <View style={{ flexDirection: 'row' }}>{renderStars(r.overall_rating, 14)}</View>}
              </View>
              {r.comments && <Text style={sp.reviewComments} numberOfLines={3}>{r.comments}</Text>}
              {r.review_date && <Text style={sp.reviewDate}>{new Date(r.review_date).toLocaleDateString('en-GB')}</Text>}
            </View>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const sp = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  ratingHero: { margin: 16, borderRadius: 20, padding: 28, alignItems: 'center' },
  ratingLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: '600', marginBottom: 8 },
  ratingBig: { color: '#fff', fontSize: 64, fontWeight: '900', lineHeight: 72 },
  ratingDesc: { color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: '600', marginTop: 8 },
  section: { marginHorizontal: 16, marginBottom: 16 },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: '#1e293b', marginBottom: 12 },
  warnCard: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  warnIcon: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  warnType: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  warnReason: { fontSize: 12, color: '#64748b', marginTop: 2 },
  warnDate: { fontSize: 11, color: '#94a3b8', marginTop: 4 },
  severityBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, alignSelf: 'flex-start' },
  severityText: { fontSize: 10, fontWeight: '800' },
  competencyRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  competencyName: { fontSize: 13, fontWeight: '600', color: '#1e293b' },
  reviewCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  reviewHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  reviewPeriod: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  reviewComments: { fontSize: 13, color: '#64748b', lineHeight: 20 },
  reviewDate: { fontSize: 11, color: '#94a3b8', marginTop: 6 },
  empty: { color: '#94a3b8', textAlign: 'center', padding: 24 },
});
